// PWA install state — tracks whether we should surface the "Install SpotDrop"
// CTA. Uses the `beforeinstallprompt` event on Android/desktop Chrome and
// falls back to iOS Safari's "Share → Add to Home Screen" instructions.
//
// Persists dismissal in localStorage so we don't nag users after they close it.

import React from "react";
import { reportInstallEvent } from "./campaign";

const KEY_DISMISSED = "spotdrop_install_dismissed_at";
const DISMISS_TTL_MS = 1000 * 60 * 60 * 24 * 30; // 30 days
const KEY_INSTALLED = "spotdrop_install_completed";

function readDismissed() {
  try {
    const raw = localStorage.getItem(KEY_DISMISSED);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (Number.isFinite(ts) && Date.now() - ts < DISMISS_TTL_MS) return true;
  } catch {
    /* ignore */
  }
  return false;
}

function markDismissed() {
  try {
    localStorage.setItem(KEY_DISMISSED, String(Date.now()));
  } catch {
    /* ignore */
  }
}

function markInstalled() {
  try {
    localStorage.setItem(KEY_INSTALLED, "1");
  } catch {
    /* ignore */
  }
}

function isInstalled() {
  try {
    if (localStorage.getItem(KEY_INSTALLED) === "1") return true;
  } catch {
    /* ignore */
  }
  if (typeof window === "undefined") return false;
  // iOS Safari sets navigator.standalone; other browsers report via matchMedia.
  if (typeof window.matchMedia === "function") {
    try {
      if (window.matchMedia("(display-mode: standalone)").matches) return true;
    } catch {
      /* ignore */
    }
  }
  return !!window.navigator?.standalone;
}

function isIOS() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator?.userAgent || "";
  // iPad now reports macOS + touch — cover both cases.
  const iOSLegacy = /iPad|iPhone|iPod/.test(ua);
  const iPadMac =
    ua.includes("Mac") &&
    typeof window.navigator?.maxTouchPoints === "number" &&
    window.navigator.maxTouchPoints > 1;
  return iOSLegacy || iPadMac;
}

/** React hook returning `{ canPrompt, isIOS, isInstalled, promptInstall,
 *  showIOSInstructions, setShowIOSInstructions, dismiss }`.
 *  - `canPrompt` is true only when the browser fired `beforeinstallprompt`.
 *  - `promptInstall()` triggers the native prompt or opens iOS instructions.
 *  - `dismiss()` remembers the user's choice for 30 days.
 */
export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = React.useState(null);
  const [installed, setInstalled] = React.useState(() => isInstalled());
  const [dismissed, setDismissed] = React.useState(() => readDismissed());
  const [showIOSInstructions, setShowIOSInstructions] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;
    // If we're running in standalone (already installed via any path — Android
    // beforeinstallprompt or iOS Add-to-Home-Screen — report once). Backend
    // enforces once-per-device via a unique index.
    if (installed) {
      reportInstallEvent();
    }
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      markInstalled();
      setInstalled(true);
      setDeferredPrompt(null);
      // Fire-and-forget campaign event (once-per-device on the backend).
      reportInstallEvent();
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const promptInstall = React.useCallback(async () => {
    if (deferredPrompt) {
      let outcome = null;
      try {
        deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;
        outcome = choice?.outcome;
        if (outcome === "accepted") {
          markInstalled();
          setInstalled(true);
        }
        // If the user dismissed the native prompt, do NOT persist the 30-day
        // dismissal. Chrome consumes the event either way; we just clear
        // `deferredPrompt` and keep the button visible so they can retry on
        // the next visit (when Chrome refires beforeinstallprompt).
      } catch {
        /* prompt() throws InvalidStateError if the event was already consumed
           — treat as no-op and keep the button available. */
      } finally {
        // Only clear once we've handled userChoice (or errored).
        setDeferredPrompt(null);
      }
      return;
    }
    // No native prompt — fall back to iOS Safari / manual-install instructions.
    setShowIOSInstructions(true);
  }, [deferredPrompt]);

  const dismiss = React.useCallback(() => {
    // "Got it" on the iOS sheet — close the sheet WITHOUT persisting a 30-day
    // dismissal so the user can retry from any other CTA later.
    setShowIOSInstructions(false);
  }, []);

  return {
    canPrompt: !!deferredPrompt,
    isIOS: isIOS(),
    isInstalled: installed,
    dismissed,
    promptInstall,
    showIOSInstructions,
    setShowIOSInstructions,
    dismiss,
  };
}
